import { MODAL_HOOK_CONTEXT_ERROR } from "@constants/constants";
import CloseIcon from "@icons/close-icon.svg?react";
import * as Dialog from "@radix-ui/react-dialog";
import type { FunctionComponent, ReactNode } from "react";
import {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";

import styles from "./modal.module.scss";

interface Props {
	open: boolean;
	handleOpen: () => void;
	title: string;
	subtitle?: string;
	withClose?: boolean;
	children?: ReactNode;
	maxWidth?: number;
}

const ModalContext = createContext<Props | undefined>(undefined);

export function useModalContext() {
	const context = useContext(ModalContext);

	if (context) return context;

	throw new Error(MODAL_HOOK_CONTEXT_ERROR);
}

export function useModal() {
	const [show, setShow] = useState(false);

	const handleShow = useCallback(() => {
		setShow((prev) => !prev);
	}, []);

	return { show, handleShow };
}

export function Modal(props: Props) {
	const {
		open,
		handleOpen,
		title,
		subtitle,
		withClose,
		children,
		maxWidth = 480,
	} = props;

	const memoizedContextValue = useMemo(
		() => ({ open, handleOpen, title, subtitle, withClose, maxWidth }),
		[open, handleOpen, title, subtitle, withClose, maxWidth],
	);

	return (
		<ModalContext.Provider value={memoizedContextValue}>
			<Dialog.Root open={open} onOpenChange={handleOpen}>
				<Dialog.Portal>
					<Dialog.Overlay className={styles.container}>
						<Dialog.Content
							className={styles.modal}
							style={{ maxWidth: `${maxWidth}px` }}
							onInteractOutside={(e) => {
								const target = e.target as HTMLElement;

								const path = e.composedPath() as HTMLElement[];
								const isDropdownClick = path.some(
									(el) =>
										el?.hasAttribute?.("data-dropdown") ||
										el?.hasAttribute?.("data-dropdown-list") ||
										el?.getAttribute?.("role") === "listbox" ||
										el?.getAttribute?.("role") === "option" ||
										el?.classList?.contains?.("listContainer"),
								);

								if (
									isDropdownClick ||
									target.closest('[data-dropdown="true"]') ||
									target.closest('[data-dropdown-list="true"]') ||
									target.closest('[role="listbox"]') ||
									target.closest('[role="option"]') ||
									target.closest(".listContainer") ||
									target.classList.contains("listContainer") ||
									target.hasAttribute("data-dropdown-list")
								) {
									e.preventDefault();
									return;
								}
							}}
							onPointerDownOutside={(e) => {
								const target = e.target as HTMLElement;

								const path = e.composedPath() as HTMLElement[];
								const isDropdownClick = path.some(
									(el) =>
										el?.hasAttribute?.("data-dropdown") ||
										el?.hasAttribute?.("data-dropdown-list") ||
										el?.getAttribute?.("role") === "listbox" ||
										el?.getAttribute?.("role") === "option" ||
										el?.classList?.contains?.("listContainer"),
								);

								if (
									isDropdownClick ||
									target.closest('[data-dropdown="true"]') ||
									target.closest('[data-dropdown-list="true"]') ||
									target.closest('[role="listbox"]') ||
									target.closest('[role="option"]') ||
									target.closest(".listContainer") ||
									target.classList.contains("listContainer") ||
									target.hasAttribute("data-dropdown-list")
								) {
									e.preventDefault();
									return;
								}
							}}
						>
							{withClose && (
								<Dialog.Close className={styles.icon_container} asChild>
									<button type="button" aria-label="Close">
										<CloseIcon className={styles.close_icon} />
									</button>
								</Dialog.Close>
							)}
							<div className={styles.modal_header}>
								<div className={styles.header_container}>
									<Dialog.Title asChild>
										<h2 className={styles.title}>{title}</h2>
									</Dialog.Title>
								</div>
								{Boolean(subtitle) && (
									<Dialog.Description asChild>
										<p className={styles.subtitle}>{subtitle}</p>
									</Dialog.Description>
								)}
							</div>
							{children}
						</Dialog.Content>
					</Dialog.Overlay>
				</Dialog.Portal>
			</Dialog.Root>
		</ModalContext.Provider>
	);
}

interface ModalHeader {
	children: ReactNode;
}

export const ModalBody: FunctionComponent<ModalHeader> = ({ children }) => {
	return <div className={styles.modal_body}>{children}</div>;
};

export const ModalFooter: FunctionComponent<ModalHeader> = ({ children }) => {
	return <div className={styles.modal_footer}>{children}</div>;
};

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
